import type { ProtectedRouteDecision } from '../../../contexts/app-session-state'
import type { CreateUserStatus } from '../hooks/useCreateUserMutation'

export type AuthCompleteRouteDecision =
  | 'loading'
  | 'onboarding'
  | 'auto-create'
  | 'create-user'
  | 'purchase'
  | 'post-payment-onboarding'
  | 'tabs'
  | 'error'

export function resolveAuthCompleteRoute(params: {
  isAuthenticated: boolean
  isAuthLoading: boolean
  protectedRoute: ProtectedRouteDecision
  isPendingSignupFirstName: boolean
  hasResolvedFirstName: boolean
  createUserStatus: CreateUserStatus
  requiresPostPaymentOnboarding: boolean
  hasPurchased: boolean
  isAccessPending: boolean
}): AuthCompleteRouteDecision {
  const {
    isAuthenticated,
    isAuthLoading,
    protectedRoute,
    isPendingSignupFirstName,
    hasResolvedFirstName,
    createUserStatus,
    requiresPostPaymentOnboarding,
    hasPurchased,
    isAccessPending,
  } = params

  if (
    isAuthLoading ||
    protectedRoute === 'loading' ||
    isPendingSignupFirstName ||
    createUserStatus === 'pending'
  ) {
    return 'loading'
  }

  if (!isAuthenticated || protectedRoute === 'onboarding') {
    return 'onboarding'
  }

  if (protectedRoute === 'error') {
    return 'error'
  }

  if (protectedRoute === 'create-user') {
    if (createUserStatus === 'success') {
      return resolvePostCreateDestination({
        isAccessPending,
        hasPurchased,
        requiresPostPaymentOnboarding,
      })
    }

    if (createUserStatus === 'error' || !hasResolvedFirstName) {
      return 'create-user'
    }

    return 'auto-create'
  }

  return resolvePostCreateDestination({
    isAccessPending,
    hasPurchased,
    requiresPostPaymentOnboarding,
  })
}

function resolvePostCreateDestination(params: {
  isAccessPending: boolean
  hasPurchased: boolean
  requiresPostPaymentOnboarding: boolean
}): AuthCompleteRouteDecision {
  if (params.isAccessPending) {
    return 'loading'
  }

  if (!params.hasPurchased) {
    return 'purchase'
  }

  if (params.requiresPostPaymentOnboarding) {
    return 'post-payment-onboarding'
  }

  return 'tabs'
}
